import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import VerifiedOutlined from '@mui/icons-material/VerifiedOutlined';
import { theme } from '@omnigenbiodata/ui';
import { Participant, SampleKit } from '../../../../../../core/types/common.types';
import DetailRow from '../DetailRow';

const primaryTint = theme.palette.grey[100];

interface FinalConfirmationPanelProps {
  participant: Participant;
  sample: SampleKit;
  onConfirm: () => void;
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function FinalConfirmationPanel({ participant, sample, onConfirm }: FinalConfirmationPanelProps) {
  return (
    <Paper
      elevation={4}
      sx={{
        mt: 4,
        border: 1,
        borderColor: 'rgba(16, 53, 68, 0.18)',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ height: 8, bgcolor: 'primary.main' }} />

      <Box p={3}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: primaryTint,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
            aria-hidden="true"
          >
            <VerifiedOutlined color="primary" sx={{ fontSize: 30 }} />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: 'primary.main' }}>
              Confirm Sample and Participant
            </Typography>

            <Typography variant="body1" component="p" color="text.secondary">
              Check the participant and sample kit details before creating the link.
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" component="h3" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
              Participant
            </Typography>

            <Box component="dl" sx={{ m: 0 }}>
              <DetailRow label="Participant Ref" value={participant.participantRef} />
              <DetailRow label="First Name" value={participant.firstName} />
              <DetailRow label="Surname" value={participant.surname} />
              <DetailRow label="DOB" value={participant.dob} />
              <DetailRow label="Sex at Birth" value={participant.sexAtBirth} />
              <DetailRow label="NHS Number" value={participant.nhsNumber} />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" component="h3" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
              Sample Kit
            </Typography>

            <Box component="dl" sx={{ m: 0 }}>
              <DetailRow label="Sample Barcode" value={sample.sampleBarcode} />

              {sample.linkDate && <DetailRow label="Link Date" value={formatDateTime(sample.linkDate)} />}
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          startIcon={<CheckCircleOutlined />}
          onClick={onConfirm}
          sx={{
            minHeight: 72,
            fontSize: '1.25rem',
            fontWeight: 700,
          }}
        >
          Confirm sample and participant
        </Button>
      </Box>
    </Paper>
  );
}

export default FinalConfirmationPanel;
